import { rtkApi as api } from '../rtkApi';

export interface Proposal {
  _id: string;
  proposalId: string;
  contractId: string;
  clauseId: string;
  clauseHierarchicalId: string;
  originalValue: any;
  proposedValue: any;
  status: 'pending' | 'accepted' | 'rejected' | 'countered' | 'expired';
  proposedBy: 'buyer' | 'seller' | 'agent';
  responseRequiredFrom: 'buyer' | 'seller' | 'agent';
  message?: string;
  response?: {
    message?: string;
    respondedAt?: string;
    respondedBy?: 'buyer' | 'seller' | 'agent';
  };
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    _id: string;
    name: string;
    email: string;
    role: string;
  };
}

export interface CreateProposalRequest {
  contractId: string;
  clauseId: string;
  clauseHierarchicalId: string;
  originalValue: any;
  proposedValue: any;
  message?: string;
  expiresAt?: string;
}

export interface RespondToProposalRequest {
  status: 'accepted' | 'rejected' | 'countered';
  message?: string;
}

export interface ProposalSummary {
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
  countered: number;
  expired: number;
}

export const proposalApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Create a new proposal
    createProposal: builder.mutation<
      { success: boolean; proposal: Proposal },
      CreateProposalRequest
    >({
      query: (requestData) => ({
        url: '/proposals',
        method: 'POST',
        data: requestData,
      }),
      invalidatesTags: ['Proposal', 'ProposalSummary'],
    }),

    // Create multiple proposals at once (batch)
    createBatchProposals: builder.mutation<
      {
        success: boolean;
        proposals: Proposal[];
        count: number;
        errors?: any[];
      },
      { contractId: string; proposals: CreateProposalRequest[] }
    >({
      query: (requestData) => ({
        url: '/proposals/batch',
        method: 'POST',
        data: requestData,
      }),
      invalidatesTags: ['Proposal', 'ProposalSummary'],
    }),

    // Get all proposals for a contract
    getContractProposals: builder.query<
      { success: boolean; proposals: Proposal[] },
      { contractId: string; status?: string }
    >({
      query: ({ contractId, status }) => ({
        url: `/proposals/contract/${contractId}${
          status ? `?status=${status}` : ''
        }`,
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'Proposal', id: contractId },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // Get proposals for a specific clause
    getClauseProposals: builder.query<
      { success: boolean; proposals: Proposal[] },
      { contractId: string; clauseId: string; status?: string }
    >({
      query: ({ contractId, clauseId, status }) => ({
        url: `/proposals/contract/${contractId}/clause/${clauseId}${
          status ? `?status=${status}` : ''
        }`,
      }),
      providesTags: (result, error, { contractId, clauseId }) => [
        { type: 'Proposal', id: `${contractId}-${clauseId}` },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // Respond to a proposal
    respondToProposal: builder.mutation<
      { success: boolean; proposal: Proposal },
      { proposalId: string; data: RespondToProposalRequest }
    >({
      query: ({ proposalId, data: responseData }) => ({
        url: `/proposals/${proposalId}/respond`,
        method: 'PUT',
        data: responseData,
      }),
      invalidatesTags: ['Proposal', 'ProposalSummary'],
    }),

    // Get proposals requiring response from current user
    getProposalsRequiringResponse: builder.query<
      { success: boolean; proposals: Proposal[] },
      { contractId: string }
    >({
      query: ({ contractId }) => ({
        url: `/proposals/contract/${contractId}/requiring-response`,
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'Proposal', id: `${contractId}-requiring-response` },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // Get proposals made by current user
    getProposalsByUser: builder.query<
      { success: boolean; proposals: Proposal[] },
      { contractId: string }
    >({
      query: ({ contractId }) => ({
        url: `/proposals/contract/${contractId}/by-user`,
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'Proposal', id: `${contractId}-by-user` },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // Get proposal summary for a contract
    getProposalSummary: builder.query<
      { success: boolean; summary: ProposalSummary },
      { contractId: string }
    >({
      query: ({ contractId }) => ({
        url: `/proposals/contract/${contractId}/summary`,
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'ProposalSummary', id: contractId },
        { type: 'ProposalSummary', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useCreateProposalMutation,
  useCreateBatchProposalsMutation,
  useGetContractProposalsQuery,
  useGetClauseProposalsQuery,
  useRespondToProposalMutation,
  useGetProposalsRequiringResponseQuery,
  useGetProposalsByUserQuery,
  useGetProposalSummaryQuery,
} = proposalApi;
